import React, { useState, useEffect, useMemo } from "react";
import { generateClient } from "aws-amplify/api";
import { getSubjects } from "../../graphql/queries";

const client = generateClient();

const safeJsonParse = (val, fallback) => {
  if (val == null) return fallback;
  if (typeof val === "string") {
    try {
      return JSON.parse(val);
    } catch {
      return fallback;
    }
  }
  return val;
};

const WorkflowsSection = ({ subjectId }) => {
  const purple = "#2E2868";

  const [subjectData, setSubjectData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchSubjectData = async () => {
      if (!subjectId) {
        if (isMounted) {
          setSubjectData(null);
        }
        return;
      }

      try {
        const response = await client.graphql({
          query: getSubjects,
          variables: { id: [parseInt(subjectId, 10)] },
        });

        const subjectsList = response?.data?.getSubjects?.subjects || [];
        if (isMounted) setSubjectData(subjectsList[0] || null);
      } catch (error) {
        console.error("Error fetching subject data:", error);
        if (isMounted) setSubjectData(null);
      }
    };

    fetchSubjectData();
    return () => {
      isMounted = false;
    };
  }, [subjectId]);

  const workflows = subjectData?.subject_workflows || [];

  const overallStep = useMemo(() => {
    if (!subjectData) return "No Data";
    if (subjectData.next_touchpoint) return subjectData.next_touchpoint;
    if (subjectData.last_touchpoint) return subjectData.last_touchpoint;

    const wf0 = workflows[0];
    if (!wf0) return "No Active Steps";
    return wf0.next_touchpoint || wf0.last_touchpoint || "In Progress";
  }, [subjectData, workflows]);

  const getWorkflowBadge = (wf) => {
    const tasksList = safeJsonParse(wf?.workflow_tasks_json, []);
    const completionMap = safeJsonParse(wf?.tasks_json, {});

    if (!Array.isArray(tasksList) || tasksList.length === 0) {
      return wf?.next_touchpoint || wf?.last_touchpoint || "Active";
    }

    const nextTask = tasksList.find((t) => {
      const rec = completionMap?.[t.id];
      return !rec || rec.outcome == null;
    });

    if (!nextTask) return "Completed";

    return (
      nextTask.purpose ||
      nextTask.action?.text_intro ||
      nextTask.action?.action ||
      "In Progress"
    );
  };

  return (
    <div className="my-3" style={{ maxWidth: "490px" }}>
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <span
              style={{ fontSize: "30px", color: "#b2a9ff", marginRight: "6px" }}
            >
              ⋮⋮
            </span>
            <h5
              className="fw-bold m-0"
              style={{ color: purple, cursor: "pointer" }}
              onClick={() => setOpen(!open)}
            >
              Workflows
            </h5>
          </div>

          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              backgroundColor: purple,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.3s ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
            onClick={() => setOpen(!open)}
          >
            <i className="bi bi-chevron-right" style={{ fontSize: 14 }} />
          </div>
        </div>

        {open && (
          <div className="d-flex flex-column gap-3">
            <div
              className="p-3"
              style={{
                background: "#eef0ff",
                borderRadius: "12px",
                border: `1px solid ${purple}`,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#6c757d",
                  marginBottom: "4px",
                }}
              >
                CURRENT OVERALL STEP
              </div>
              <div
                style={{ fontSize: "18px", fontWeight: "bold", color: purple }}
              >
                {overallStep}
              </div>
            </div>

            <hr className="m-0" style={{ opacity: 0.1 }} />

            <div
              style={{ fontSize: "14px", fontWeight: "600", color: "#6c757d" }}
            >
              Active Workflows
            </div>

            {workflows.length > 0 ? (
              workflows.map((wf, i) => (
                <div
                  key={wf.id || i}
                  className="p-3"
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "12px",
                    borderLeft: `4px solid ${purple}`,
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div style={{ fontWeight: 700, color: purple }}>
                      {wf.workflow_name || "Unnamed Workflow"}
                    </div>

                    <span
                      className="badge bg-secondary"
                      style={{ fontSize: "10px" }}
                    >
                      {getWorkflowBadge(wf)}
                    </span>
                  </div>

                  <div
                    className="mt-2"
                    style={{ fontSize: "12px", color: "#6c757d" }}
                  >
                    Updated:{" "}
                    {wf.updated_at
                      ? new Date(wf.updated_at).toLocaleString()
                      : "—"}
                  </div>
                </div>
              ))
            ) : (
              <div
                className="text-center text-muted"
                style={{ fontSize: "13px" }}
              >
                No active workflows found.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowsSection;
